/**
 * Batch 18-J23 — Provider Audit History Admin Panel
 *
 * Read-only operator surface for provider admin audit history.
 * Renders inside `TrainingCatalogAdmin` under the
 * `Provider Audit History` tab. Backed entirely by the J3 provider
 * read RPC + the J21 safe audit-history read RPC. Never reads the
 * base audit table directly. Never displays raw secret material,
 * raw payloads, payload hashes, signature status, idempotency keys,
 * raw full external email, or any HMAC/private-key material — the
 * J21 RPC already strips these via per-action allow-list projection,
 * and this panel adds a defense-in-depth UI key blocklist.
 *
 * Scope (per J22 plan):
 *   - provider selector via `list_training_providers_for_admin` (J3)
 *     with an `All manageable providers` option
 *   - audit list via
 *     `list_training_provider_admin_audit_log_for_admin` (J21)
 *   - action / actor UUID / target-type filters
 *   - load-more pagination (clamp-200 in J21)
 *   - safe metadata rendered as key/value rows with a hard
 *     blocklist on unsafe keys
 *
 * Out of scope (intentionally deferred):
 *   - audit row mutation / rollback / undo
 *   - reprocess from audit detail
 *   - unsafe JSON blob viewer for the snapshot metadata
 *   - actor directory lookup (J21 v1 returns display name + email
 *     as null; UI falls back to the actor UUID, then to
 *     "System / unknown actor")
 *   - details drawer / dialog (deferred per the J22 plan; the row
 *     itself already surfaces every safe field)
 */

import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Loader2 } from 'lucide-react';

// ─── Types (narrow local cast — generated Supabase types do not
//     yet know about the J21 RPC) ─────────────────────────────────────

export interface TrainingProviderAdminRow {
  provider_id: string;
  org_id: string;
  shop_id: string | null;
  provider_key: string;
  display_name: string;
  provider_type: string;
  auth_type: string;
  timestamp_tolerance_seconds: number;
  is_active: boolean;
  secret_configured: boolean;
  public_key_configured: boolean;
  created_at: string;
  updated_at: string;
  last_received_at: string | null;
  event_count: number | string;
  has_unmatched_events: boolean;
}

export interface ProviderAuditHistoryRow {
  audit_id: string;
  provider_id: string | null;
  provider_key: string | null;
  provider_display_name: string | null;
  action: string;
  target_type: string | null;
  target_id: string | null;
  actor_user_id: string | null;
  actor_display_name: string | null;
  actor_email_masked: string | null;
  reason: string | null;
  safe_summary: string;
  safe_metadata: Record<string, unknown> | null;
  created_at: string;
}

interface ListProvidersRpc {
  (
    fn: 'list_training_providers_for_admin',
    args: {
      p_org_id: string;
      p_shop_id: string | null;
      p_include_inactive: boolean;
    },
  ): Promise<{
    data: TrainingProviderAdminRow[] | null;
    error: { message: string; code?: string } | null;
  }>;
}

interface ListAuditRpc {
  (
    fn: 'list_training_provider_admin_audit_log_for_admin',
    args: {
      p_provider_id: string | null;
      p_action_filter: string | null;
      p_actor_user_id: string | null;
      p_target_type: string | null;
      p_limit: number;
      p_offset: number;
    },
  ): Promise<{
    data: ProviderAuditHistoryRow[] | null;
    error: { message: string; code?: string } | null;
  }>;
}

interface ProviderAuditHistoryAdminPanelProps {
  orgId: string | null | undefined;
  shopId: string | null | undefined;
  // Kept for prop parity with the other J-lane admin panels; the
  // audit history panel is strictly read-only so the actor user is
  // never sent to a mutation RPC.
  currentUserId?: string | null | undefined;
}

// ─── Safe error mapper — never echoes raw Postgres error text. ─────

function safeRpcError(
  error: { message: string; code?: string } | null,
): string {
  if (!error) return 'Unknown error';
  if (error.code === '42501') {
    return 'You do not have permission to view provider audit history.';
  }
  const msg = (error.message ?? '').trim();
  return msg.length === 0 ? 'Action failed.' : msg;
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// ─── Backend action tokens (must match J5 CHECK + J6/J7 writes) ────

const ACTION_ARTIFACT_MAPPING_UPSERTED =
  'training_provider_artifact_mapping.upserted';
const ACTION_USER_MAPPING_UPSERTED =
  'training_provider_user_mapping.upserted';
const ACTION_ACTIVE_STATE_CHANGED =
  'training_provider.active_state_changed';
const ACTION_SECRET_REF_ROTATED =
  'training_provider.secret_ref_rotated';
const ACTION_EVENT_REPROCESSED =
  'training_provider_event.reprocessed';

// ─── Safe metadata helpers ─────────────────────────────────────────

// Defense-in-depth: even though the J21 helper strips these keys
// per-action, the UI refuses to render them if anything slips
// through. Keep this list aligned with the J21 deny-list.
const UNSAFE_METADATA_KEYS = new Set<string>([
  'raw_payload_jsonb',
  'payload_hash',
  'signature_status',
  'idempotency_key',
  'raw_full_external_email',
  'external_user_email',
  'secret_ref',
  'secret',
  'hmac',
  'private_key',
  'stack',
  'stack_trace',
  'error_stack',
]);

export function renderSafeMetadataValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (Array.isArray(value)) {
    if (value.every((v) => typeof v === 'string')) {
      return (value as string[]).join(', ');
    }
    return 'Multiple values';
  }
  return 'Sanitized object';
}

// ═══════════════════════════════════════════════════════════════════
// Top-level panel
// ═══════════════════════════════════════════════════════════════════

export function ProviderAuditHistoryAdminPanel({
  orgId,
  shopId,
}: ProviderAuditHistoryAdminPanelProps) {
  const queryClient = useQueryClient();

  const [includeInactiveProviders, setIncludeInactiveProviders] = useState(false);
  // null sentinel = "All manageable providers". Empty string in the
  // <select> value is mapped to null for the RPC call.
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(
    null,
  );
  const [selectedAction, setSelectedAction] = useState<string>(''); // '' = all
  const [actorUuidInput, setActorUuidInput] = useState<string>('');
  const [selectedTargetType, setSelectedTargetType] = useState<string>(''); // '' = all
  const [page, setPage] = useState<number>(0);
  const pageSize = 100;

  const enabled = Boolean(orgId);

  // ── Provider list ────────────────────────────────────────────────
  const providersQuery = useQuery({
    queryKey: [
      'training-providers-admin',
      orgId ?? null,
      shopId ?? null,
      includeInactiveProviders,
    ],
    enabled,
    queryFn: async (): Promise<TrainingProviderAdminRow[]> => {
      const rpc = supabase.rpc.bind(supabase) as unknown as ListProvidersRpc;
      const { data, error } = await rpc('list_training_providers_for_admin', {
        p_org_id: orgId as string,
        p_shop_id: shopId ?? null,
        p_include_inactive: includeInactiveProviders,
      });
      if (error) throw new Error(safeRpcError(error));
      return data ?? [];
    },
  });

  // ── Derived filter values for the audit query ────────────────────
  const actorUuidTrimmed = actorUuidInput.trim();
  const actorUuidIsValid =
    actorUuidTrimmed.length === 0 || UUID_RE.test(actorUuidTrimmed);
  const actorUuidForRpc: string | null =
    actorUuidTrimmed.length > 0 && UUID_RE.test(actorUuidTrimmed)
      ? actorUuidTrimmed
      : null;
  const actionForRpc: string | null =
    selectedAction.length > 0 ? selectedAction : null;
  const targetTypeForRpc: string | null =
    selectedTargetType.length > 0 ? selectedTargetType : null;

  // ── Audit list (paginated; load-more appends to local state) ────
  const auditQuery = useQuery({
    queryKey: [
      'training-provider-audit-history-admin',
      selectedProviderId,
      actionForRpc,
      actorUuidForRpc,
      targetTypeForRpc,
      page,
    ],
    enabled,
    queryFn: async (): Promise<ProviderAuditHistoryRow[]> => {
      const rpc = supabase.rpc.bind(supabase) as unknown as ListAuditRpc;
      const { data, error } = await rpc(
        'list_training_provider_admin_audit_log_for_admin',
        {
          p_provider_id: selectedProviderId,
          p_action_filter: actionForRpc,
          p_actor_user_id: actorUuidForRpc,
          p_target_type: targetTypeForRpc,
          p_limit: pageSize,
          p_offset: page * pageSize,
        },
      );
      if (error) throw new Error(safeRpcError(error));
      return data ?? [];
    },
  });

  // Append rows when `page` increases so the operator gets a
  // load-more feed instead of paged replacement. Filter changes
  // reset `page` to 0 and replace the visible set.
  const [accumulated, setAccumulated] = useState<ProviderAuditHistoryRow[]>([]);
  useEffect(() => {
    // Reset on filter change (any change other than `page`).
    setAccumulated([]);
    setPage(0);
    // The query key already includes filters, so React Query
    // refetches on its own.
    queryClient.removeQueries({
      queryKey: ['training-provider-audit-history-admin'],
      exact: false,
    });
  }, [
    selectedProviderId,
    actionForRpc,
    actorUuidForRpc,
    targetTypeForRpc,
    queryClient,
  ]);

  useEffect(() => {
    if (!auditQuery.data) return;
    if (page === 0) {
      setAccumulated(auditQuery.data);
    } else {
      setAccumulated((prev) => [...prev, ...(auditQuery.data ?? [])]);
    }
  }, [auditQuery.data, page]);

  const noMoreRows =
    auditQuery.data != null && auditQuery.data.length < pageSize;

  const providerOptions = providersQuery.data ?? [];

  // ── Permission-denied detection (non-leaky) ─────────────────────
  const auditError = auditQuery.error as Error | undefined;
  const auditPermissionDenied =
    auditError != null &&
    /permission|42501/i.test(auditError.message);

  // ── Render ───────────────────────────────────────────────────────

  if (!enabled) {
    return (
      <Card>
        <CardContent
          className="py-12 text-center text-sm text-muted-foreground"
          data-testid="provider-audit-history-admin-panel"
        >
          You do not have access to provider audit history.
        </CardContent>
      </Card>
    );
  }

  return (
    <div
      className="space-y-4"
      data-testid="provider-audit-history-admin-panel"
    >
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Provider Audit History</CardTitle>
          <CardDescription>
            Read-only provider admin action history. Audit details are
            read-only history, not an approval workflow. Provider audit history
            is operational visibility — it does not verify training and does
            not create attestation. The list shows Audit history rows with a
            Safe summary, the actor and target, and the Created at timestamp.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[260px]">
              <Label
                htmlFor="provider-audit-history-provider-select"
                className="text-xs"
              >
                Provider
              </Label>
              <select
                id="provider-audit-history-provider-select"
                data-testid="provider-audit-history-provider-select"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={selectedProviderId ?? ''}
                onChange={(e) =>
                  setSelectedProviderId(e.target.value || null)
                }
              >
                <option value="">All manageable providers</option>
                {providerOptions.map((p) => (
                  <option key={p.provider_id} value={p.provider_id}>
                    {p.display_name} — {p.provider_key}
                    {!p.is_active ? ' [inactive]' : ''}
                  </option>
                ))}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Switch
                data-testid="provider-audit-history-include-inactive-providers"
                checked={includeInactiveProviders}
                onCheckedChange={(v) =>
                  setIncludeInactiveProviders(Boolean(v))
                }
              />
              Include inactive providers
            </label>
            <div className="min-w-[220px]">
              <Label
                htmlFor="provider-audit-history-action-filter"
                className="text-xs"
              >
                Provider admin action
              </Label>
              <select
                id="provider-audit-history-action-filter"
                data-testid="provider-audit-history-action-filter"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={selectedAction}
                onChange={(e) => setSelectedAction(e.target.value)}
              >
                <option value="">All actions</option>
                <option value={ACTION_ARTIFACT_MAPPING_UPSERTED}>
                  Artifact mapping updated
                </option>
                <option value={ACTION_USER_MAPPING_UPSERTED}>
                  User mapping updated
                </option>
                <option value={ACTION_ACTIVE_STATE_CHANGED}>
                  Provider active state changed
                </option>
                <option value={ACTION_SECRET_REF_ROTATED}>
                  Provider secret reference rotated
                </option>
                <option value={ACTION_EVENT_REPROCESSED}>
                  Provider event reprocessed
                </option>
              </select>
            </div>
            <div className="min-w-[220px]">
              <Label
                htmlFor="provider-audit-history-actor-filter"
                className="text-xs"
              >
                Actor (UUID)
              </Label>
              <Input
                id="provider-audit-history-actor-filter"
                data-testid="provider-audit-history-actor-filter"
                placeholder="00000000-0000-0000-0000-000000000000"
                value={actorUuidInput}
                onChange={(e) => setActorUuidInput(e.target.value)}
                autoComplete="off"
              />
              {!actorUuidIsValid && (
                <p
                  className="mt-1 text-xs text-destructive"
                  data-testid="provider-audit-history-actor-filter-invalid"
                >
                  Enter a valid UUID or clear the field.
                </p>
              )}
            </div>
            <div className="min-w-[220px]">
              <Label
                htmlFor="provider-audit-history-target-type-filter"
                className="text-xs"
              >
                Target type
              </Label>
              <select
                id="provider-audit-history-target-type-filter"
                data-testid="provider-audit-history-target-type-filter"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={selectedTargetType}
                onChange={(e) => setSelectedTargetType(e.target.value)}
              >
                <option value="">All target types</option>
                <option value="training_provider_artifact_map">
                  training_provider_artifact_map
                </option>
                <option value="training_provider_user_map">
                  training_provider_user_map
                </option>
                <option value="training_providers">training_providers</option>
                <option value="training_provider_evidence_events">
                  training_provider_evidence_events
                </option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {auditQuery.isLoading && page === 0 && (
        <Card>
          <CardContent className="py-8 flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading audit history…
          </CardContent>
        </Card>
      )}

      {auditError && (
        <Card>
          <CardContent
            className="py-8 text-center text-sm text-muted-foreground"
            data-testid={
              auditPermissionDenied
                ? 'provider-audit-history-permission-denied'
                : 'provider-audit-history-list-error'
            }
          >
            {auditPermissionDenied
              ? 'You do not have permission to view provider audit history.'
              : 'Unable to load audit history right now.'}
          </CardContent>
        </Card>
      )}

      {!auditQuery.isLoading &&
        !auditError &&
        accumulated.length === 0 && (
          <Card>
            <CardContent
              className="py-8 text-center text-sm text-muted-foreground"
              data-testid="provider-audit-history-empty"
            >
              No audit rows match the current filters.
            </CardContent>
          </Card>
        )}

      {!auditError && accumulated.length > 0 && (
        <Card>
          <CardContent className="pt-4">
            <ul
              data-testid="provider-audit-history-list"
              className="space-y-2"
            >
              {accumulated.map((row) => (
                <AuditRow key={row.audit_id} row={row} />
              ))}
            </ul>

            <div className="mt-4 flex items-center justify-center">
              {noMoreRows ? (
                <span
                  className="text-xs text-muted-foreground"
                  data-testid="provider-audit-history-no-more-rows"
                >
                  No more rows.
                </span>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="provider-audit-history-load-more"
                  disabled={auditQuery.isFetching}
                  onClick={() => setPage((p) => p + 1)}
                >
                  {auditQuery.isFetching && (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  )}
                  Load more
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Per-row component
// ═══════════════════════════════════════════════════════════════════

function AuditRow({ row }: { row: ProviderAuditHistoryRow }) {
  const actorLabel = useMemo(() => {
    if (row.actor_display_name && row.actor_display_name.trim().length > 0) {
      return row.actor_display_name;
    }
    if (row.actor_email_masked && row.actor_email_masked.trim().length > 0) {
      return row.actor_email_masked;
    }
    if (row.actor_user_id) return row.actor_user_id;
    return 'System / unknown actor';
  }, [row.actor_display_name, row.actor_email_masked, row.actor_user_id]);

  const reasonLabel =
    row.reason && row.reason.trim().length > 0
      ? row.reason
      : 'No reason recorded';

  // Defense-in-depth: filter out any unsafe keys the J21 RPC should
  // have already stripped. If they appear, the UI refuses to render
  // them.
  const safeMetadataEntries = useMemo(() => {
    if (!row.safe_metadata || typeof row.safe_metadata !== 'object') {
      return [] as Array<[string, unknown]>;
    }
    return Object.entries(row.safe_metadata).filter(
      ([k]) => !UNSAFE_METADATA_KEYS.has(k),
    );
  }, [row.safe_metadata]);

  return (
    <li
      data-testid="provider-audit-history-row"
      className="rounded-lg border bg-card p-3 space-y-2"
    >
      <div className="flex flex-wrap items-center gap-2">
        <Badge
          variant="outline"
          data-testid="provider-audit-history-action"
          className="text-xs"
          title={row.action}
        >
          {row.action}
        </Badge>
        <span
          data-testid="provider-audit-history-summary"
          className="text-sm font-medium"
        >
          {row.safe_summary}
        </span>
        <span className="text-xs text-muted-foreground">·</span>
        <span
          data-testid="provider-audit-history-created-at"
          className="text-xs text-muted-foreground"
        >
          {row.created_at}
        </span>
      </div>

      <dl className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
        <div className="flex gap-2">
          <dt>Provider:</dt>
          <dd>
            {row.provider_display_name ?? '—'}{' '}
            {row.provider_key && (
              <code className="font-mono text-xs">{row.provider_key}</code>
            )}
          </dd>
        </div>
        <div className="flex gap-2">
          <dt>Actor:</dt>
          <dd
            data-testid="provider-audit-history-actor"
            className="font-mono text-xs"
          >
            {actorLabel}
          </dd>
        </div>
        <div className="flex gap-2">
          <dt>Target type:</dt>
          <dd className="font-mono text-xs">{row.target_type ?? '—'}</dd>
        </div>
        <div className="flex gap-2">
          <dt>Target ID:</dt>
          <dd className="font-mono text-xs">{row.target_id ?? '—'}</dd>
        </div>
      </dl>

      <div className="text-xs">
        <span className="text-muted-foreground">Reason: </span>
        <span
          data-testid="provider-audit-history-reason"
          className="break-words"
        >
          {reasonLabel}
        </span>
      </div>

      {safeMetadataEntries.length > 0 && (
        <div className="rounded-md border bg-muted/30 p-2 text-xs space-y-0.5">
          <p className="text-muted-foreground">Safe metadata</p>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-0.5">
            {safeMetadataEntries.map(([k, v]) => (
              <div key={k} className="flex gap-2">
                <dt className="font-mono">{k}:</dt>
                <dd className="font-mono break-all">
                  {renderSafeMetadataValue(v)}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </li>
  );
}
